"use client";

import { LomdaV3Block } from "@/lib/utils/lomdaV3/types";
import { cn } from "@/lib/utils";
import { useLomdaV3Editor } from "../../store/useLomdaV3Editor";
import { useResolvedBlockWidth } from "../../hooks/useGridGap";
import { buildBlockOverrideVars } from "../../lib/theme";
import TextBlock from "./text/textBlock";
import MediaLayoutBlock from "./media/mediaLayoutBlock";
import AudioBlock from "./media/audioBlock";
import EmbedBlock from "./media/embedBlock";
import HtmlBlock from "./media/htmlBlock";
import AttachmentBlock from "./media/attachmentBlock";
import WalkthroughBlock from "./walkthrough/walkthroughBlock";
import AccordionBlock from "./interactive/accordionBlock";
import TabsBlock from "./interactive/tabsBlock";
import ChecklistBlock from "./interactive/checklistBlock";
import FlipCardsBlock from "./interactive/flipCardsBlock";
import ScenarioBlock from "./interactive/scenarioBlock";
import CardSortingBlock from "./interactive/cardSortingBlock";
import HowToBlock from "./interactive/howToBlock";
import HotspotBlock from "./interactive/hotspotBlock";
import KnowledgeCheckBlock from "./interactive/knowledgeCheckBlock";
import StepsBlock from "./interactive/stepsBlock";
import RatingBlock from "./interactive/ratingBlock";
import PollBlock from "./interactive/pollBlock";
import WordCloudBlock from "./interactive/wordCloudBlock";
import OpenEndedBlock from "./interactive/openEndedBlock";
import CompleteSentenceBlock from "./interactive/completeSentenceBlock";
import ChartBlock from "./interactive/chartBlock";
import ColumnsBlock from "./layout/columnsBlock";
import SpacerBlock from "./layout/spacerBlock";
import DividerBlock from "./layout/dividerBlock";
import ButtonBlock from "./layout/buttonBlock";
import { BlockErrorBoundary } from "./blockErrorBoundary";
import {
    BandColumn,
    BandText,
    bandStyle,
    blockBackground,
    hasSectionBackground,
} from "./layout/sectionBand";
import { useBlockEditable } from "../../hooks/useBlockEditable";

export default function BlockView({ block }: { block: LomdaV3Block }) {
    const override = useLomdaV3Editor(
        (s) => s.content.blockThemes?.[block.id]
    );
    const width = useResolvedBlockWidth(block.id);
    const background = blockBackground(block);
    const editable = useBlockEditable();
    const patchBlockBackground = useLomdaV3Editor(
        (s) => s.patchBlockBackground
    );
    const inner = override ? (
        <div style={buildBlockOverrideVars(override)}>{renderBlock(block)}</div>
    ) : (
        renderBlock(block)
    );
    // Only wrap when the width is actually reduced — a full-width block keeps its
    // original DOM so drag-and-drop measurement is unchanged.
    const sized =
        width < 100 ? (
            <div className="mx-auto w-full" style={{ maxWidth: `${width}%` }}>
                {inner}
            </div>
        ) : (
            inner
        );

    // A section background leaves the page's centred column and fills the page,
    // then puts that column back around the text — so the words stay exactly
    // where they were while the colour/image runs edge to edge.
    //
    // Once the text is placed by hand it is positioned against the BAND, so it
    // leaves that column: an author dragging a caption onto a photo is working
    // against the picture, not against the text grid.
    const banded = hasSectionBackground(background) ? (
        <div
            className="relative flex flex-col justify-center"
            // No padding of its own: an "auto" band must sit exactly where
            // the same block without a background would. Breathing room is what
            // the Fixed and Full heights are for.
            style={bandStyle(background!)}
        >
            {/* Always the same chain — BandColumn/BandText never swap places.
                A placed box is absolute against the BAND, so the column in
                between simply stops applying to it. Changing the tree instead
                would remount the box mid-drag and drop the pointer capture. */}
            <BandColumn bleeding={background!.fullWidth !== false}>
                <BandText
                    background={background!}
                    width={width}
                    editable={editable}
                    onMove={(textX, textY) =>
                        patchBlockBackground(block.id, { textX, textY })
                    }
                >
                    {inner}
                </BandText>
            </BandColumn>
        </div>
    ) : (
        sized
    );

    return <BlockErrorBoundary key={block.id}>{banded}</BlockErrorBoundary>;
}

function renderBlock(block: LomdaV3Block) {
    switch (block.type) {
        case "text":
            return <TextBlock block={block} />;
        case "media-layout":
            return <MediaLayoutBlock block={block} />;
        case "audio":
            return <AudioBlock block={block} />;
        case "embed":
            return <EmbedBlock block={block} />;
        case "html":
            return <HtmlBlock block={block} />;
        case "attachment":
            return <AttachmentBlock block={block} />;
        case "walkthrough":
            return <WalkthroughBlock block={block} />;
        case "accordion":
            return <AccordionBlock block={block} />;
        case "tabs":
            return <TabsBlock block={block} />;
        case "checklist":
            return <ChecklistBlock block={block} />;
        case "flip-cards":
            return <FlipCardsBlock block={block} />;
        case "scenario":
            return <ScenarioBlock block={block} />;
        case "card-sorting":
            return <CardSortingBlock block={block} />;
        case "how-to":
            return <HowToBlock block={block} />;
        case "hotspot":
            return <HotspotBlock block={block} />;
        case "knowledge-check":
            return <KnowledgeCheckBlock block={block} />;
        case "steps":
            return <StepsBlock block={block} />;
        case "columns":
            return <ColumnsBlock block={block} />;
        case "rating":
            return <RatingBlock block={block} />;
        case "poll":
            return <PollBlock block={block} />;
        case "word-cloud":
            return <WordCloudBlock block={block} />;
        case "open-ended":
            return <OpenEndedBlock block={block} />;
        case "complete-sentence":
            return <CompleteSentenceBlock block={block} />;
        case "chart":
            return <ChartBlock block={block} />;
        case "spacer":
            return <SpacerBlock block={block} />;
        case "divider":
            return <DividerBlock block={block} />;
        case "button":
            return <ButtonBlock block={block} />;
    }
}
